import { useCallback, useState } from "react";

import { queryClient } from "@config/queryClient";
import usePlatformStore from "@store/usePlatformStore";
import { useAddRelation, useDeleteRelation } from "@screens/thread/hooks/useRelations";
import {
  DISCUSSED_TOPICS_QUERY_KEY,
  MEETING_QUERY_KEY,
  OBJECT_TAGS_QUERY_KEY,
  SUGGESTED_TAGGING_OPTIONS_QUERY_KEY,
  TAGGING_OPTIONS_QUERY_KEY,
} from "@config/query";
import type { OptionsForTags, Tag as TagType } from "@interfaces/tag.interface";
import { ITagResponse } from "@interfaces/responses.interface";
import { IRelationResponse } from "@interfaces/relation.interface";
import { RelationObjectType } from "@enums/relations.enum";

export function useOptimisticTags(
  contentType: RelationObjectType,
  objectId: string,
  initialTags: ITagResponse[] = [],
  relationType: string | null = null
) {
  const { itemId } = usePlatformStore();
  const [currentTags, setCurrentTags] = useState(initialTags);
  const [temporaryTags, setTemporaryTags] = useState<IRelationResponse[]>([]);

  const { mutate: addTag } = useAddRelation(objectId);
  const { mutate: removeTag } = useDeleteRelation(objectId);

  const updateTags = useCallback(
    (newTags: TagType[]) => {
      if (!objectId) return;

      let prevSuggestedData: OptionsForTags | null = null;

      queryClient.setQueryData<OptionsForTags>(
        [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY, objectId],
        (oldData) => {
          if (!oldData) return oldData;
          prevSuggestedData = structuredClone(oldData);

          return {
            ...oldData,
            deals: oldData.deals.filter((d) => !newTags.some((tag) => tag.id === d.id)),
            people: oldData.people.filter((p) => !newTags.some((tag) => tag.id === p.id)),
          };
        }
      );

      setCurrentTags((prevTags) => {
        const existingIds = new Set(prevTags.map((tag) => tag.object.id));
        const uniqueNewTags = newTags.filter((tag) => !existingIds.has(tag.id));
        const updatedPrevTags = prevTags.map((prevTag) => {
          const matchingNewTag = newTags.find((newTag) => newTag.id === prevTag.object.id);

          if (matchingNewTag && prevTag.is_suggested) {
            return { ...prevTag, is_suggested: false };
          }
          return prevTag;
        });

        const formattedTags: ITagResponse[] = uniqueNewTags.map((tag) => ({
          id: tag.id,
          object: tag,
          object_type: tag.object_type,
          created: new Date(),
          modified: new Date(),
          pending: true,
        }));

        return [...updatedPrevTags, ...formattedTags];
      });

      newTags.forEach((tag) => {
        const optimisticTagId = tag.id;

        addTag(
          {
            left_content_type: contentType,
            left_object_id: objectId,
            right_content_type: tag.object_type as RelationObjectType,
            right_object_id: tag.id,
            relation_type: relationType,
          },
          {
            onSuccess: async (response) => {
              await queryClient.invalidateQueries({
                queryKey: [OBJECT_TAGS_QUERY_KEY, contentType, objectId],
              });
              void queryClient.invalidateQueries({ queryKey: [MEETING_QUERY_KEY, itemId] });
              void queryClient.invalidateQueries({ queryKey: [TAGGING_OPTIONS_QUERY_KEY] });
              void queryClient.invalidateQueries({
                queryKey: [DISCUSSED_TOPICS_QUERY_KEY, contentType, objectId],
              });
              void queryClient.invalidateQueries({
                queryKey: [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY, objectId],
              });

              setTemporaryTags((prev) => [...prev, response]);

              // Update tags' state to non-pending
              setCurrentTags((prevTags) =>
                prevTags.map((t) =>
                  t.id === response.right_object_id ? { ...t, pending: false } : t
                )
              );
            },
            onError: () => {
              if (prevSuggestedData) {
                queryClient.setQueryData(
                  [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY, objectId],
                  prevSuggestedData
                );
              }

              setCurrentTags((prevTags) => prevTags.filter((t) => t.object.id !== optimisticTagId));
            },
          }
        );
      });
    },
    [objectId, addTag]
  );

  const onRemoveTag = useCallback(
    (tag: ITagResponse) => {
      if (!objectId) return;

      const tagId = tag.id;

      const relation = temporaryTags.find((t) => t.right_object_id === tagId);

      setCurrentTags((prev) => prev.filter((t) => t.id !== tagId));

      // An in-flight tags fetch predates this delete; its response would re-show the removed tag.
      void queryClient.cancelQueries({ queryKey: [OBJECT_TAGS_QUERY_KEY, contentType, objectId] });
      void queryClient.cancelQueries({
        queryKey: [DISCUSSED_TOPICS_QUERY_KEY, contentType, objectId],
      });

      removeTag(relation?.id || tagId, {
        // Refetch on failure too: a cancelled tags fetch would otherwise leave the tag hidden though it still exists.
        onSettled: () => {
          void queryClient.invalidateQueries({
            queryKey: [OBJECT_TAGS_QUERY_KEY, contentType, objectId],
          });
          void queryClient.invalidateQueries({ queryKey: [TAGGING_OPTIONS_QUERY_KEY] });
          void queryClient.invalidateQueries({
            queryKey: [DISCUSSED_TOPICS_QUERY_KEY, contentType, objectId],
          });
        },
      });
    },
    [objectId, currentTags, temporaryTags, removeTag]
  );

  return {
    currentTags,
    updateTags,
    onRemoveTag,
    setCurrentTags,
    temporaryTags,
  };
}
